import React, { useCallback, useState } from 'react';
import { Box } from '@material-ui/core';
import { TreeNode } from 'react-dropdown-tree-select';
import { useSnackbar, VariantType } from 'notistack';
import { useStyles } from '../../custom-forms';
import { useTranslate } from '../../hooks/useTranslate';
import {
  CaseCalculationPart,
  CaseInfo,
  ManipulationTariffPercentResponse,
  OrganizationBranch,
  TariffPercentResponse
} from '../../interfaces/Case';
import {
  WorkflowCalculation,
  WorkflowOptimization,
  WorkflowSupplier
} from '../../interfaces/Workflow';
import { IReferential } from '../../interfaces/IReferential';
import { Organization } from '../../interfaces/Organization';
import { Order } from '../../interfaces/Order';
import {
  BulkSelectionMode,
  CalculationActions,
  CalculationTotals,
  ColumnFilter,
  ColumnKey,
  FiltersState,
  SortState
} from './types/alternative-calculation.types';
import { buildGridTemplate } from './config/columns';
import { useVariantsEnabled } from './context/CalculationFlagsContext';
import AlternativeCalculationVehicleBand from './components/AlternativeCalculationVehicleBand';
import AlternativeCalculationToolbar from './components/AlternativeCalculationToolbar';
import AlternativeCalculationPieces from './components/AlternativeCalculationPieces';
import AlternativeCalculationOrderedParts from './components/AlternativeCalculationOrderedParts';
import AlternativeCalculationFooter from './components/AlternativeCalculationFooter';

export interface AlternativeCalculationContentProps {
  parts: CaseCalculationPart[];
  orderedParts: CaseCalculationPart[];
  currency: IReferential | null;
  maintenancePackage: IReferential | null;
  totals: CalculationTotals;
  deliveryAddresses: OrganizationBranch[];
  actions: CalculationActions;
  partQualityTypes: IReferential[];
  supplierList: Organization[];
  nonStockSuppliers: WorkflowSupplier[];
  isAddDialogOpen: boolean;
  isAddDialog: boolean;
  newCalculationPart: CaseCalculationPart;
  onCloseAddDialog: () => void;
  onSubmitCalculationPart: (part: CaseCalculationPart) => void;
  onClose: () => void;
  onSave: (parts: CaseCalculationPart[]) => void;
  disabled: boolean;
  isLoading: boolean;
  onDropDownChange: (currentNode: TreeNode, selectedNodes: TreeNode[]) => void;
  calculationHistoryId: number;
  workflowCalculation: WorkflowCalculation | null;
  workflowOptimization: WorkflowOptimization[];
  currentWorkflowContainsOrderFormOrInterogatePartner: boolean;
  orderList: Order[];
  caseInfo: CaseInfo | null;
  manipulationTariff: ManipulationTariffPercentResponse;
  insurerList: IReferential[];
  serviceId: number | null;
  additionTariff: TariffPercentResponse;
  discountTariff: TariffPercentResponse;
  packageCalculationId: number | null;
}

export const AlternativeCalculationContent: React.FC<AlternativeCalculationContentProps> = ({
  parts,
  orderedParts,
  currency,
  maintenancePackage,
  totals,
  deliveryAddresses,
  actions,
  partQualityTypes,
  supplierList,
  nonStockSuppliers,
  isAddDialogOpen,
  isAddDialog,
  newCalculationPart,
  onCloseAddDialog,
  onSubmitCalculationPart,
  onClose,
  onSave: onSaveProp,
  disabled,
  isLoading,
  onDropDownChange,
  workflowCalculation,
  workflowOptimization,
  currentWorkflowContainsOrderFormOrInterogatePartner
}) => {
  const t = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const notify = useCallback(
    (message: string, variant: VariantType) => enqueueSnackbar(message, { variant }),
    [enqueueSnackbar]
  );

  const c = useStyles();
  const [sort, setSort] = useState<SortState>({ column: null, dir: 1 });
  const [filters, setFilters] = useState<FiltersState>({});
  const [activeMode, setActiveMode] = useState<BulkSelectionMode>(null);

  const variantsEnabled = useVariantsEnabled();
  const showNetPrice = workflowCalculation?.alternativeNetPrice ?? false;
  const gridStyle = {
    ['--alt-grid-cols']: buildGridTemplate(showNetPrice, variantsEnabled)
  } as React.CSSProperties;

  const onSort = useCallback(
    (key: ColumnKey) =>
      setSort((prev) =>
        prev.column !== key
          ? { column: key, dir: 1 }
          : prev.dir === 1
          ? { column: key, dir: -1 }
          : { column: null, dir: 1 }
      ),
    []
  );

  const onApplyFilter = useCallback(
    (key: ColumnKey, value: ColumnFilter | null) =>
      setFilters((prev) => {
        const next = { ...prev };
        if (value == null) delete next[key];
        else next[key] = value;
        return next;
      }),
    []
  );

  const onRemoveFilter = useCallback(
    (key: ColumnKey) =>
      setFilters((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      }),
    []
  );

  const onClearFilters = useCallback(() => setFilters({}), []);

  const onUnselectAll = () => {
    actions.unselectAll();
    setActiveMode(null);
    notify(t('ALT_CALC_UNSELECTED_ALL', 'Selecția a fost anulată pentru toate piesele'), 'info');
  };

  const handleSave = () => onSaveProp(parts);

  const onDeliveryChange = (currentNode: TreeNode, selectedNodes: TreeNode[]) => {
    onDropDownChange(currentNode, selectedNodes);
    setActiveMode(null);
    onClearFilters();
  };

  return (
    <Box
      className={c.pageBg}
      style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...gridStyle
      }}
    >
      <Box style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14, flex: 1 }}>
        <AlternativeCalculationVehicleBand
          totals={totals}
          maintenancePackage={maintenancePackage}
          currency={currency}
        />
        <AlternativeCalculationToolbar
          parts={parts}
          activeMode={activeMode}
          deliveryAddresses={deliveryAddresses}
          workflowCalculation={workflowCalculation}
          workflowOptimization={workflowOptimization}
          currentWorkflowContainsOrderFormOrInterogatePartner={
            currentWorkflowContainsOrderFormOrInterogatePartner
          }
          actions={actions}
          onNotify={notify}
          onActiveMode={setActiveMode}
          onDeliveryChange={onDeliveryChange}
          onClearFilters={onClearFilters}
        />
        <AlternativeCalculationPieces
          parts={parts}
          sort={sort}
          filters={filters}
          workflowCalculation={workflowCalculation}
          partQualityTypes={partQualityTypes}
          supplierList={supplierList}
          nonStockSuppliers={nonStockSuppliers}
          isAddDialogOpen={isAddDialogOpen}
          isAddDialog={isAddDialog}
          newCalculationPart={newCalculationPart}
          actions={actions}
          onNotify={notify}
          onSort={onSort}
          onApplyFilter={onApplyFilter}
          onRemoveFilter={onRemoveFilter}
          onClearFilters={onClearFilters}
          onUnselectAll={onUnselectAll}
          onCloseAddDialog={onCloseAddDialog}
          onSubmitCalculationPart={onSubmitCalculationPart}
        />
        {orderedParts.length > 0 && (
          <AlternativeCalculationOrderedParts
            parts={orderedParts}
            supplierList={supplierList}
            partQualityTypes={partQualityTypes}
            currency={currency}
          />
        )}
      </Box>

      <AlternativeCalculationFooter
        parts={parts}
        totals={totals}
        currency={currency}
        disabled={disabled}
        isLoading={isLoading}
        onClose={onClose}
        onSave={handleSave}
      />
    </Box>
  );
};

export default AlternativeCalculationContent;
